import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { CheckOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';
import { introduction, PAGE_TITLE, partnerBenefits, silkExperiences, tiers } from './benefits';
import './style.scss';

function Description({ benefit }) {
  if (!benefit.highlight) return benefit.description;
  const [before, after] = benefit.description.split(benefit.highlight);
  return <>{before}<strong>{benefit.highlight}</strong>{after}</>;
}

function BenefitGroup({ title, benefits, included, onDetails }) {
  return (
    <section className="mo-benefit-group">
      <h3>{title}</h3>
      <div className="mo-benefit-list">
        {benefits.map((benefit) => (
          <button
            type="button"
            className="mo-benefit-card"
            key={benefit.id}
            aria-label={`查看权益详情：${benefit.name}`}
            aria-haspopup="dialog"
            onClick={() => onDetails(benefit)}
          >
            <span className="mo-benefit-image">
              <img src={benefit.image} alt={benefit.alt} loading="lazy" />
              {included && <span className="mo-included"><CheckOutlined /></span>}
            </span>
            <span className="mo-benefit-info">
              <span className="mo-benefit-name">{benefit.name}</span>
              <span>{benefit.description}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function PartnerCarousel({ onDetails }) {
  const swiperRef = useRef(null);
  const [active, setActive] = useState(0);

  return (
    <section className="mo-partners" aria-label="合作伙伴礼遇">
      <Swiper
        slidesPerView={1.08}
        spaceBetween={10}
        slidesOffsetBefore={16}
        slidesOffsetAfter={16}
        onSwiper={(swiper) => { swiperRef.current = swiper; }}
        onSlideChange={(swiper) => setActive(swiper.activeIndex)}
      >
        {partnerBenefits.map((benefit, index) => (
          <SwiperSlide key={benefit.id}>
            <article className="mo-partner-card" aria-label={benefit.name}>
              <img src={benefit.image} alt={benefit.alt} loading="lazy" />
              <div className="mo-partner-content">
                <h3>{benefit.name}</h3>
                <p><Description benefit={benefit} /></p>
                <button
                  className="mo-detail-link"
                  type="button"
                  aria-label={`查看权益详情：${benefit.name}`}
                  aria-haspopup="dialog"
                  tabIndex={index === active ? 0 : -1}
                  onClick={() => onDetails(benefit)}
                >
                  查看权益详情 <RightOutlined />
                </button>
              </div>
            </article>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="mo-carousel-controls">
        <button type="button" aria-label="上一项礼遇" title="上一项礼遇" disabled={active === 0} onClick={() => swiperRef.current?.slidePrev()}><LeftOutlined /></button>
        <div className="mo-carousel-dots">
          {partnerBenefits.map((benefit, index) => (
            <button
              key={benefit.id}
              type="button"
              aria-label={`显示${benefit.name}`}
              aria-current={active === index ? 'true' : undefined}
              title={benefit.name}
              onClick={() => swiperRef.current?.slideTo(index)}
            ><span /></button>
          ))}
        </div>
        <button type="button" aria-label="下一项礼遇" title="下一项礼遇" disabled={active === partnerBenefits.length - 1} onClick={() => swiperRef.current?.slideNext()}><RightOutlined /></button>
      </div>
    </section>
  );
}

export default function MandarinOrientalBenefits() {
  const [activeTier, setActiveTier] = useState(0);
  const [detail, setDetail] = useState(null);
  const [pinned, setPinned] = useState(false);
  const tabsRef = useRef(null);
  const markerRef = useRef(null);
  const pageRef = useRef(null);
  const tier = tiers[activeTier];

  useEffect(() => {
    if (!window.IntersectionObserver) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { root: pageRef.current.closest('#container'), threshold: 0 });
    observer.observe(markerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!detail) return undefined;
    // The application scrolls #container, not the document body locked by Modal.
    const scrollContainer = pageRef.current.closest('#container');
    if (!scrollContainer) return undefined;
    const previousOverflow = scrollContainer.style.overflowY;
    scrollContainer.style.overflowY = 'hidden';
    return () => { scrollContainer.style.overflowY = previousOverflow; };
  }, [detail]);

  function selectTier(index, focus = false) {
    setActiveTier(index);
    const tabs = tabsRef.current;
    const scrollContainer = pageRef.current.closest('#container');
    // Keep the new panel visible when changing tiers from a pinned tab bar.
    if (pinned && scrollContainer) {
      const offset = markerRef.current.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top;
      scrollContainer.scrollTop += offset;
    }
    if (focus) tabs.querySelectorAll('[role="tab"]')[index].focus();
  }

  function handleTabKey(event) {
    const directions = { ArrowRight: 1, ArrowLeft: -1 };
    if (event.key in directions) {
      event.preventDefault();
      selectTier((activeTier + directions[event.key] + tiers.length) % tiers.length, true);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      selectTier(event.key === 'Home' ? 0 : tiers.length - 1, true);
    }
  }

  return (
    <main className="mo-benefits" ref={pageRef}>
      <header className="mo-heading">
        <h1 aria-label={PAGE_TITLE}>尚扇荟 <span>(Fans of M.O.)</span><span>会员礼遇</span></h1>
        <div className="mo-introduction">
          {introduction.map((text) => <p key={text}>{text}</p>)}
        </div>
      </header>
      <div className="mo-tabs-marker" ref={markerRef} />
      <div className={`mo-tabs${pinned ? ' is-pinned' : ''}`} role="tablist" aria-label="会员级别" ref={tabsRef} onKeyDown={handleTabKey}>
        {tiers.map((item, index) => (
          <button
            type="button"
            key={item.id}
            id={`mo-tab-${item.id}`}
            role="tab"
            aria-selected={activeTier === index}
            aria-controls={`mo-panel-${item.id}`}
            tabIndex={activeTier === index ? 0 : -1}
            onClick={() => selectTier(index)}
          >{item.label}</button>
        ))}
      </div>
      {tiers.map((item, index) => (
        <div key={item.id} id={`mo-panel-${item.id}`} role="tabpanel" aria-labelledby={`mo-tab-${item.id}`} hidden={activeTier !== index}>
          {activeTier === index && (
            <>
              <section className="mo-tier-intro mo-inset">
                <h2 className="pb-30">{tier.name}</h2>
                {tier.introduction.map((text) => <p key={text}>{text}</p>)}
              </section>
              <div className="mo-tier-image mo-inset">
                <img src={tier.image} alt={tier.alt} />
              </div>
              {tier.included && (
                <>
                  <section className="mo-stay mo-inset">
                    <div className="mo-section-heading">
                      <h2>{tier.benefitsTitle}<br />入住专享</h2>
                      {tier.benefitsDescription.map((text) => <p key={text}>{text}</p>)}
                    </div>
                    <BenefitGroup title="每次莅临" benefits={tier.included} included onDetails={setDetail} />
                    <BenefitGroup title="可选两项特别礼遇*视供应情况而定" benefits={tier.optional} onDetails={setDetail} />
                  </section>
                  <section className="mo-always mo-inset">
                    <h2>{tier.partnerTitle}</h2>
                    <p>{tier.partnerDescription}</p>
                  </section>
                  {tier.id === 'silk' && (
                    <div className="mo-experiences mo-inset">
                      {silkExperiences.map((benefit) => (
                        <button
                          key={benefit.id}
                          type="button"
                          className="mo-experience"
                          style={{ backgroundImage: `url(${benefit.image})` }}
                          aria-label={`查看权益详情：${benefit.name}`}
                          aria-haspopup="dialog"
                          onClick={() => setDetail(benefit)}
                        >
                          <span className="mo-experience-name">{benefit.name}</span>
                          <span>{benefit.description}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <PartnerCarousel key={tier.id} onDetails={setDetail} />
                </>
              )}
            </>
          )}
        </div>
      ))}
      <Modal
        open={Boolean(detail)}
        title={detail?.name}
        onCancel={() => setDetail(null)}
        footer={null}
        centered
        width={420}
        rootClassName="mo-benefits-modal"
      >
        {detail && (
          <>
            <img className="mo-detail-image" src={detail.image} alt={detail.alt || detail.name} />
            <p><Description benefit={detail} /></p>
            {detail.name.endsWith('*') && <p className="mo-detail-note">*视供应情况而定</p>}
          </>
        )}
      </Modal>
    </main>
  );
}
